import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import styles from "./Select.module.css";

export interface Option {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  /**
   * Render the selected value in a monospace font (12.5px) — for ID-like
   * values such as asset IDs. Defaults to false (body font).
   */
  monospaceValue?: boolean;
  /**
   * Extra class on the root wrapper — lets a host override the default
   * `min-width: 180px` (e.g. compact selects inside a grid column).
   */
  className?: string;
}

interface MenuPosition {
  top: number;
  left: number;
  width: number;
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  id,
  monospaceValue = false,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [pos, setPos] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const selected = options.find((o) => o.value === value);

  // Position the portal-rendered listbox under the trigger.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [open]);

  // Reposition on viewport changes while open.
  useEffect(() => {
    if (!open) return;
    function update() {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  // Close on outside click (covers both trigger and portal-rendered listbox).
  useEffect(() => {
    if (!open) return;
    function close(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (listboxRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  function onKey(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (open) {
        onChange(options[activeIdx].value);
        setOpen(false);
      } else {
        setOpen(true);
        setActiveIdx(Math.max(0, options.findIndex((o) => o.value === value)));
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(options.length - 1, i + 1));
      if (!open) setOpen(true);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    }
  }

  return (
    <div className={`${styles.root} ${className ?? ""}`}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKey}
      >
        {selected ? (
          <span className={monospaceValue ? styles.valueMono : undefined}>
            {selected.label}
          </span>
        ) : (
          <span className={styles.placeholder}>{placeholder ?? "Select…"}</span>
        )}
        <ChevronDown size={16} aria-hidden className={open ? styles.chevronOpen : styles.chevron} />
      </button>
      {open && pos &&
        createPortal(
          <ul
            ref={listboxRef}
            role="listbox"
            className={styles.listbox}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
            }}
          >
            {options.map((o, i) => (
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={`${styles.option} ${i === activeIdx ? styles.active : ""}`}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
